/** Zwei Arbeitskarten der Auftragsdetails: fehlende Artikelzuordnung und Trageriemen-Farbe. */
import { useState } from "react";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchSelect } from "@/components/SearchSelect";
import { PackageCheck } from "lucide-react";

export const isLineOpen = (l) => !l.variant_id && !l.bundle_id && !l.messe_bundle_id;
export const hasMesseLine = (lines) => (lines || []).some((l) => !!l.messe_bundle_id);

export const AssignCard = ({ order, money, assignSel, setAssignSel, assignOptions, onAssign,
  assignQual, setAssignQual }) => {
  const { t } = useI18n();
  const [typeSel, setTypeSel] = useState({});
  const nProd = assignOptions.filter((o) => o.value.startsWith("v:")).length;
  const nBundle = assignOptions.filter((o) => o.value.startsWith("b:")).length;
  const nMesse = assignOptions.filter((o) => o.value.startsWith("m:")).length;
  const optsFor = (i) => {
    const tf = typeSel[i] || "all";
    if (tf === "produkt") return assignOptions.filter((o) => o.value.startsWith("v:"));
    if (tf === "bundle") return assignOptions.filter((o) => o.value.startsWith("b:"));
    if (tf === "messe") return assignOptions.filter((o) => o.value.startsWith("m:"));
    return assignOptions;
  };
  return (
    <div className="rounded-md border-2 border-chart-3/50 bg-chart-3/5 shadow-sm overflow-hidden" data-testid="assign-card">
      <div className="px-4 py-3 border-b border-border font-heading font-medium flex items-center gap-2 text-chart-3">
        <PackageCheck className="h-4 w-4" /> {t("od.assignRequired")}
      </div>
      <div className="p-4 space-y-3">
        <p className="text-sm text-muted-foreground">{t("od.assignHint")}</p>
        {order.lines.map((l, i) => isLineOpen(l) ? (
          <div key={i} className="border-t border-border pt-3 space-y-2">
            <div className="text-sm">
              <div className="font-medium">{l.name}</div>
              <div className="text-xs text-muted-foreground">{t("common.quantity")} {l.quantity} · {money(l.unit_price)}
                {l.suggested_sku
                  ? <span className="ml-1 text-chart-4">· {t("ord.suggestion")}: {l.suggested_sku}{l.match_confidence ? ` (${Math.round(l.match_confidence * 100)}%)` : ""}</span>
                  : <span className="ml-1 text-chart-3">· {t("od.noSuggestionFound")}</span>}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={typeSel[i] || "all"}
                onValueChange={(v) => { setTypeSel({ ...typeSel, [i]: v }); setAssignSel({ ...assignSel, [i]: "" }); }}>
                <SelectTrigger className="w-40 shrink-0" data-testid={`assign-type-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("od.typeAll")} ({assignOptions.length})</SelectItem>
                  <SelectItem value="produkt">{t("od.typeProduct")} ({nProd})</SelectItem>
                  <SelectItem value="bundle">{t("od.typeBundle")} ({nBundle})</SelectItem>
                  <SelectItem value="messe">{t("od.typeMesse")} ({nMesse})</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex-1 min-w-[220px]">
                <SearchSelect testId={`assign-select-${i}`} options={optsFor(i)}
                  value={assignSel[i] ?? (l.suggested_messe_bundle_id ? `m:${l.suggested_messe_bundle_id}`
                    : (l.suggested_bundle_id ? `b:${l.suggested_bundle_id}`
                    : (l.suggested_variant_id ? `v:${l.suggested_variant_id}` : "")))}
                  onChange={(v) => setAssignSel({ ...assignSel, [i]: v })}
                  placeholder={t("ord.chooseProductBundle")} />
              </div>
              <Select value={assignQual[i] || "good"}
                onValueChange={(v) => setAssignQual({ ...assignQual, [i]: v })}>
                <SelectTrigger className="w-32 shrink-0" data-testid={`assign-quality-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="good">{t("od.firstChoice")}</SelectItem>
                  <SelectItem value="second_choice">{t("ord.secondShort")}</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" className="shrink-0" onClick={() => onAssign(i)} data-testid={`assign-btn-${i}`}>{t("od.assign")}</Button>
            </div>
          </div>
        ) : null)}
      </div>
    </div>
  );
};

export const StrapCard = ({ order, canSell, showAll, setShowAll, strapSel, setStrapSel,
  strapOpts, onAssignStrap }) => {
  const { t } = useI18n();
  const withIndex = order.lines.map((l, i) => ({ l, i }));
  const shown = showAll ? withIndex : withIndex.filter(({ l }) => l.strap_required || l.strap_variant_id || l.strap_review_required);
  return (
    <div className="rounded-md border border-border bg-card shadow-sm overflow-hidden" data-testid="strap-card">
      <div className="px-4 py-3 border-b border-border font-heading font-medium flex items-center justify-between gap-2">
        <span>{t("od.strapTitle")}</span>
        {canSell && <Button size="sm" variant="ghost" onClick={() => setShowAll((v) => !v)} data-testid="strap-toggle-all">
          {showAll ? t("od.strapOnlyMarked") : t("od.strapShowAll")}</Button>}
      </div>
      <div className="p-4 space-y-3">
        {shown.length === 0 && <p className="text-sm text-muted-foreground">{t("od.strapNone")}</p>}
        {shown.map(({ l, i }) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-[160px] text-sm">{l.name}
              {l.bundle_id && <span className="ml-1 text-xs text-primary">({t("bundle.title")})</span>}
              {(l.strap_required_quantity || 0) > 0 && <span className="ml-2 text-xs text-muted-foreground">{t("od.strapNeeded")}: {l.strap_required_quantity}</span>}
              <div className="mt-0.5">
                {l.strap_variant_sku
                  ? <span className="text-xs text-primary">{t("od.strapCurrent")}: {l.strap_variant_sku} ({l.strap_qty ?? l.strap_deducted ?? 0})</span>
                  : <span className="text-xs text-chart-3">{t("od.strapNoColor")}</span>}
                {l.strap_review_required && <span className="ml-2 text-xs font-medium text-chart-3" data-testid={`strap-review-${i}`}>· {t("od.strapReview")}</span>}
                {l.strap_variant_sku && (l.strap_mode === "stock" && (l.strap_deducted || 0) > 0
                  ? <span className="ml-2 text-xs text-primary" data-testid={`strap-status-${i}`}>· {t("od.strapBooked")}</span>
                  : (l.strap_mode === "documentation"
                    ? <span className="ml-2 text-xs text-muted-foreground" data-testid={`strap-status-${i}`}>· {t("od.strapDocumented")}</span>
                    : null))}
              </div>
            </div>
            <Select value={strapSel[i]?.variant || l.strap_variant_id || l.strap_suggested_variant_id || ""}
              onValueChange={(v) => setStrapSel({ ...strapSel, [i]: { ...strapSel[i], variant: v } })}>
              <SelectTrigger className="w-64" data-testid={`strap-select-${i}`}><SelectValue placeholder={t("od.chooseColor")} /></SelectTrigger>
              <SelectContent>{strapOpts.map((s) => <SelectItem key={s.id} value={s.id}>{s.sku} · {t("inv.available")}: {s.available}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" className="w-20 h-9" value={strapSel[i]?.qty ?? l.strap_required_quantity ?? l.quantity}
              onChange={(e) => setStrapSel({ ...strapSel, [i]: { ...strapSel[i], qty: e.target.value } })}
              data-testid={`strap-qty-${i}`} />
            {canSell && <Button size="sm" onClick={() => onAssignStrap(i)} data-testid={`strap-assign-${i}`}>{t("od.assignBtn")}</Button>}
          </div>
        ))}
        <p className="text-xs text-muted-foreground">{t("od.strapHint")}</p>
      </div>
    </div>
  );
};
